<template>
  <div>
    <v-container class="container">
      <v-row wrap>
        <v-col cols="12" lg="3" md="4" sm="4" class="return-icon" style="text-align: center">
          <img class="d-none d-sm-flex" src="../assets/images/contact-icon.svg" alt="" />
          <img class="d-flex d-sm-none" width="125.5px" height="80px" src="../assets/images/contact-icon.svg" alt="" />
        </v-col>
        <v-col cols="8" md="8" sm="8">
          <div v-if="checking" class="return-header">Confirming…</div>
          <div v-else-if="verified" class="return-header">Thank you.</div>
          <div v-else class="return-header">We couldn’t confirm your submission.</div>

          <div v-if="verified" class="return-sub-header">Your identity verification is in progress.</div>
          <div v-else-if="!checking" class="return-sub-header">There was a problem confirming your verification.</div>

          <div v-if="verified" class="return-content">
            We have received your information and are verifying your identity. You will see a message on your dashboard
            when the verification process is complete (usually within 1-2 business days). In the meantime, you can still
            access available course content.
          </div>
          <div v-else-if="!checking" class="return-content">
            Your verification was submitted, but we could not confirm it from this device. Please return to your
            dashboard — the status will update on the next dashboard refresh.
          </div>

          <div class="nextButton return-button">
            <v-btn @click="goToDashboard" :ripple="false" variant="flat" :disabled="checking">
              <span style="padding-left: 6px">Return to your dashboard</span>
              <v-icon left style="margin-right: 0">mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import { AjaxLoader } from '../mixins/ajaxLoader';
import { useAuthStore } from '../stores/auth';
import { useUserStore } from '../stores/user';

export default defineComponent({
  mixins: [AjaxLoader],

  data() {
    return {
      checking: true,
      verified: false
    };
  },

  async created() {
    const authStore = useAuthStore();
    const userStore = useUserStore();

    if (!authStore.auth) {
      this.$router.push({ name: 'WelcomeDesktop' });
      return;
    }

    // Hydrate the connector + user store after the cross-origin redirect from Aether.
    if (!Object.keys(userStore.apis.learnerData).length) {
      await this.$conn.initSecureSession();
    }

    const params = new URLSearchParams(window.location.search);
    const state = params.get('state') || '';
    const status = params.get('status') || '';
    const aetherIdvId = params.get('aether_idv_id') || '';

    const storedState = sessionStorage.getItem('idvState') || '';
    sessionStorage.removeItem('idvState');

    if (state && storedState && state !== storedState) {
      this.checking = false;
      this.verified = false;
      return;
    }

    if (status !== 'submitted') {
      this.checking = false;
      this.verified = false;
      return;
    }

    try {
      const response = await this.$conn.confirmIdVerification({
        state,
        aether_idv_id: aetherIdvId
      });
      this.verified = Boolean(response?.confirmed);
    } catch (err) {
      console.warn('confirmIdVerification failed:', err);
      this.verified = false;
    } finally {
      this.checking = false;
    }

    if (this.verified) {
      // Refresh learner data in the background so the dashboard pill shows the new status,
      // then auto-redirect after a brief moment so the learner sees the confirmation.
      this.$conn.getLearnerDetails(this.$auth.getUserId()).catch(() => {});
      setTimeout(() => {
        this.$router.push({ name: 'dashboard' });
      }, 1800);
    }
  },

  methods: {
    async goToDashboard() {
      let self = this;
      await this.$conn
        .getLearnerDetails(this.$auth.getUserId())
        .then()
        .finally(() => self.clearLoader());

      this.$router.push({ name: 'dashboard' });
    }
  }
});
</script>

<style lang="stylus">
.return-header {
  font-size: 56px;
  font-weight: 900;
  margin-bottom: 17px;
  color: #191919;
}

.return-sub-header {
  font-size: 30px;
  font-weight: 500;
  margin-bottom: 32px;
}

.return-icon {
  margin-top: 12px;
}

.return-content {
  margin-bottom: 32px;
  max-width: 673px;
  font-weight: 400;
  font-size: 18px;
}

.return-button {
  margin-bottom: 80.5px;
  margin-left: -8px;
}

@media (max-width: 600px) {
  .return-icon {
    margin: 0 0 24px 32px;
  }

  .return-header {
    font-size: 36px;
    margin: 0 0 16px 32px;
  }

  .return-sub-header {
    font-size: 18px;
    margin: 0 31px 31.6px 32px;
  }

  .return-content {
    font-size: 16px;
    margin: 0 33px 32.5px 32px;
  }

  .return-button {
    margin-bottom: 58.5px;
    margin-left: 24px;

    button {
      width: 275.7px;
      height: 45px;
    }

    span {
      font-size: 16px;
      padding-right: 11.7px;
    }
  }
}
</style>
